import type { CamposTabla, TablaConfig } from '../types/api';

/**
 * Validación en el navegador con las MISMAS reglas que worker/agente/config.py y
 * back/app/Dwh/ConfigNormalizer.php. Es solo una ayuda para el usuario: el back
 * vuelve a validar todo y es quien manda.
 */
export const NOMBRE = /^[A-Z0-9_]+$/;
export const MAX_MIN = 7 * 24 * 60;
const PROHIBIDO_EN_FILTRO = /;|--|\/\*|\b(INSERT|UPDATE|DELETE|MERGE|DROP|ALTER|CREATE|EXEC|EXECUTE|TRUNCATE|GRANT)\b/i;
const VENTANA = /^(\d{1,2})-(\d{1,2})$/;

/** Lo que edita el formulario: todo como texto o booleano. */
export interface FormTabla {
  nombre: string;
  activa: boolean;
  columnas: string;
  excluir_lob: boolean;
  frecuencia: string;
  cambios: string;
  bajas: string;
  margen: string;
  filtro: string;
  ventana: string;
  carga_unica: boolean;
  motivo: string;
  motivo_cambio: string;
}

export type Errores = Partial<Record<keyof FormTabla, string>>;

const dos = (h: number) => String(h).padStart(2, '0');

export function formVacio(defecto: CamposTabla = {}): FormTabla {
  return {
    nombre: '',
    activa: true,
    columnas: typeof defecto.columnas === 'string' ? defecto.columnas : '*',
    excluir_lob: defecto.excluir_lob ?? true,
    frecuencia: defecto.frecuencia ? String(defecto.frecuencia) : '',
    cambios: '',
    bajas: defecto.bajas ? String(defecto.bajas) : '',
    margen: defecto.margen ? String(defecto.margen) : '10',
    filtro: '',
    ventana: '',
    carga_unica: false,
    motivo: '',
    motivo_cambio: '',
  };
}

/** El formulario relleno con una tabla ya normalizada. */
export function formDesdeTabla(t: TablaConfig): FormTabla {
  return {
    nombre: t.tabla,
    activa: t.activa,
    columnas: t.columnas,
    excluir_lob: t.excluir_lob,
    frecuencia: String(t.frecuencia),
    cambios: String(t.cambios),
    bajas: String(t.bajas),
    margen: String(t.margen),
    filtro: t.filtro ?? '',
    ventana: t.ventana_ini !== null && t.ventana_fin !== null ? `${dos(t.ventana_ini)}-${dos(t.ventana_fin)}` : '',
    carga_unica: t.carga_unica,
    motivo: t.motivo,
    motivo_cambio: '',
  };
}

function minutos(valor: string, obligatorio: boolean): string | undefined {
  const v = valor.trim();
  if (v === '') return obligatorio ? 'Obligatorio.' : undefined;
  if (!/^\d+$/.test(v) || Number(v) < 1 || Number(v) > MAX_MIN) return `Número de minutos entre 1 y ${MAX_MIN}.`;
  return undefined;
}

export function validarTabla(f: FormTabla, nueva: boolean): Errores {
  const e: Errores = {};
  if (nueva && !NOMBRE.test(f.nombre.trim().toUpperCase())) e.nombre = 'Solo letras A-Z, números y _ (el nombre exacto de X3).';
  const fr = minutos(f.frecuencia, true);
  if (fr) e.frecuencia = fr;
  const ca = minutos(f.cambios, false);
  if (ca) e.cambios = ca;
  const ba = minutos(f.bajas, true);
  if (ba) e.bajas = ba;
  const ma = minutos(f.margen, false);
  if (ma) e.margen = ma;
  if (f.motivo.trim() === '') e.motivo = 'Para qué se necesita (qué panel o análisis).';
  else if (f.motivo.trim().length > 400) e.motivo = 'Como mucho 400 caracteres.';

  const cols = f.columnas.trim();
  if (cols !== '' && cols !== '*') {
    const malas = cols.split(',').map((c) => c.trim().toUpperCase()).filter((c) => c !== '' && !NOMBRE.test(c));
    if (malas.length) e.columnas = `Nombres no válidos: ${malas.join(', ')}`;
  }
  if (f.filtro.trim() !== '' && PROHIBIDO_EN_FILTRO.test(f.filtro)) {
    e.filtro = 'Solo una condición de lectura (sin ;, comentarios ni órdenes de escritura).';
  }
  const ventana = f.ventana.replace(/ /g, '');
  if (ventana !== '') {
    const m = VENTANA.exec(ventana);
    if (!m || Number(m[1]) > 23 || Number(m[2]) > 23 || m[1] === m[2]) e.ventana = "Formato 'HH-HH', por ejemplo '22-06'.";
  }
  if (f.carga_unica && ventana === '') e.carga_unica = 'La carga única necesita una ventana (es una lectura larga de la tabla entera).';
  if (f.motivo_cambio.trim() === '') e.motivo_cambio = 'Explica por qué cambias la configuración.';
  return e;
}

/** El cuerpo de la petición: números como números y lo vacío fuera (= valor por defecto). */
export function camposDesdeForm(f: FormTabla, nueva: boolean): CamposTabla {
  const n = (s: string) => (s.trim() === '' ? undefined : Number(s.trim()));
  const t = (s: string) => (s.trim() === '' ? undefined : s.trim());
  const c: CamposTabla = {
    activa: f.activa,
    columnas: t(f.columnas),
    excluir_lob: f.excluir_lob,
    frecuencia: n(f.frecuencia),
    cambios: n(f.cambios),
    bajas: n(f.bajas),
    margen: n(f.margen),
    filtro: t(f.filtro),
    ventana: t(f.ventana),
    carga_unica: f.carga_unica,
    motivo: t(f.motivo),
  };
  if (nueva) c.nombre = f.nombre.trim().toUpperCase();
  return Object.fromEntries(Object.entries(c).filter(([, v]) => v !== undefined)) as CamposTabla;
}
